import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import { LLMHomepage } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMHompage";
import { OverviewReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/OverviewReport";
import { PromptsReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/PromptsReport";
import { SentimentReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sentiment-report/SentimentReport";
import { DEFAULT_INFLUENCE_VIEW } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/consts";
import {
    DEFAULT_SORT_COLUMN,
    DEFAULT_SORT_DIRECTION,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/consts";
import { onRedirectToPromptsReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/onRedirectToPromptsReport";
import { onRedirectToLLMSourcesReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/RedirectToLLMSourcesReport";
import { SourcesReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/SourcesReport";
import {
    AI_TRACKER_DEFAULT_DURATION,
    DEFAULT_LLM,
    DEFAULT_TOPIC,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { LLMParentContainer } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMParentContainer";
import { onRedirectToLLMReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/onRedirectToLLMReport";
import { ReportContainer } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/ReportContainer";
import { LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import type { RouterStateConfig } from "single-spa/react-app/router/IRouterState";

export const LLMTrackingConfig: RouterStateConfig = {
    digitalsuite_llm_solutions_parent: {
        parent: "sw",
        url: "/digitalsuite/ai-brand-visibility?settings",
        name: "digitalsuite_llm_solutions_parent",
        abstract: true,
        component: LLMParentContainer,
        configId: "AiTracker",
        secondaryBarType3: SecondarySideBarType3.None,
        reloadOnSearch: false,
        isPrimarySidebarAlwaysOpen: true,
    },
    digitalsuite_llm_solutions_homepage: {
        parent: "digitalsuite_llm_solutions_parent",
        url: "/home",
        name: "digitalsuite_llm_solutions_homepage",
        component: LLMHomepage,
        configId: "AiTracker",
        secondaryBarType3: SecondarySideBarType3.None,
        pageId: {
            section: "ai-brand-visibility",
            subSection: "homepage",
        },
        trackingId: {
            section: "Gen AI Intelligence",
            subSection: "AI Brand Visibility",
            subSubSection: "Homepage",
            pageId: "Gen AI - Brand Visibility - Homepage",
        },
        pageTitle: "llm.homepage.title",
        reloadOnSearch: false,
        isPrimarySidebarAlwaysOpen: true,
    },
    digitalsuite_llm_solutions_report: {
        parent: "digitalsuite_llm_solutions_parent",
        url: "/report",
        name: "digitalsuite_llm_solutions_report",
        abstract: true,
        component: ReportContainer,
        configId: "AiTracker",
        secondaryBarType3: SecondarySideBarType3.None,
        reloadOnSearch: false,
        isPrimarySidebarAlwaysOpen: true,
    },
    digitalsuite_llm_solutions_brand_report: {
        parent: "digitalsuite_llm_solutions_report",
        homeState: "digitalsuite_llm_solutions_homepage",
        url: "/brand?campaignId&model&duration&topic&isDurationChanged",
        name: "digitalsuite_llm_solutions_brand_report",
        redirectTo: redirectPipe(onRedirectToLLMReport()),
        component: OverviewReport,
        configId: "AiTracker",
        secondaryBarType3: SecondarySideBarType3.None,
        pageId: {
            section: "report",
            subSection: "ai-brand-visibility",
        },
        trackingId: {
            section: "Gen AI Intelligence",
            subSection: "AI Brand Visibility",
            subSubSection: "Brand Overview",
            pageId: "Gen AI - Brand Visibility - Overview",
        },
        defaultQueryParams: {
            model: LLModel.All,
            duration: AI_TRACKER_DEFAULT_DURATION,
            topic: DEFAULT_TOPIC,
            isDurationChanged: false,
        },
        pageTitle: "llm.homepage.title",
        reloadOnSearch: false,
        isPrimarySidebarAlwaysOpen: true,
    },
    digitalsuite_llm_solutions_prompts_report: {
        parent: "digitalsuite_llm_solutions_report",
        homeState: "digitalsuite_llm_solutions_homepage",
        url: "/prompts?campaignId&model&duration&topic&topicsFilter&mentionStatuses&sentimentTone&minVisibility&maxVisibility&brands&searchTerm&isDurationChanged&domains&topSourceCategories&citationGaps&citationVolatility&latestCitationStatuses&quickFilter&coreCompetitors&change&sentimentShift&sort&asc",
        name: "digitalsuite_llm_solutions_prompts_report",
        redirectTo: redirectPipe(onRedirectToLLMReport(), onRedirectToPromptsReport()),
        component: PromptsReport,
        configId: "AiTracker",
        secondaryBarType3: SecondarySideBarType3.None,
        pageId: {
            section: "report",
            subSection: "prompts-analysis",
        },
        trackingId: {
            section: "Gen AI Intelligence",
            subSection: "AI Brand Visibility",
            subSubSection: "Prompt Analysis",
            pageId: "Gen AI - Prompts Analysis - Overview",
        },
        defaultQueryParams: {
            model: DEFAULT_LLM,
            duration: AI_TRACKER_DEFAULT_DURATION,
            topic: DEFAULT_TOPIC,
            isDurationChanged: false,
            sort: DEFAULT_SORT_COLUMN,
            asc: DEFAULT_SORT_DIRECTION,
        },
        pageTitle: "llm.homepage.title",
        reloadOnSearch: false,
        isPrimarySidebarAlwaysOpen: true,
    },
    digitalsuite_llm_solutions_sources_report: {
        parent: "digitalsuite_llm_solutions_report",
        homeState: "digitalsuite_llm_solutions_homepage",
        url: "/sources?campaignId&model&duration&topic&view&isDurationChanged&domainFilter&brandFilter&searchTerm&changeFilter",
        name: "digitalsuite_llm_solutions_sources_report",
        redirectTo: redirectPipe(onRedirectToLLMReport(), onRedirectToLLMSourcesReport()),
        component: SourcesReport,
        configId: "AiTracker",
        secondaryBarType3: SecondarySideBarType3.None,
        pageId: {
            section: "report",
            subSection: "sources-analysis",
        },
        trackingId: {
            section: "Gen AI Intelligence",
            subSection: "AI Brand Visibility",
            subSubSection: "Source Analysis",
            pageId: "Gen AI - Source Analysis - Overview",
        },
        defaultQueryParams: {
            model: DEFAULT_LLM,
            duration: AI_TRACKER_DEFAULT_DURATION,
            topic: DEFAULT_TOPIC,
            view: DEFAULT_INFLUENCE_VIEW,
            isDurationChanged: false,
        },
        pageTitle: "llm.homepage.title",
        reloadOnSearch: false,
        isPrimarySidebarAlwaysOpen: true,
    },
    digitalsuite_llm_solutions_sentiment_report: {
        parent: "digitalsuite_llm_solutions_report",
        homeState: "digitalsuite_llm_solutions_homepage",
        url: "/sentiment?campaignId&model&duration&topic&isDurationChanged",
        name: "digitalsuite_llm_solutions_sentiment_report",
        redirectTo: redirectPipe(onRedirectToLLMReport()),
        component: SentimentReport,
        configId: "AiTracker",
        secondaryBarType3: SecondarySideBarType3.None,
        pageId: {
            section: "report",
            subSection: "sentiment-analysis",
        },
        trackingId: {
            section: "Gen AI Intelligence",
            subSection: "AI Brand Visibility",
            subSubSection: "Sentiment Analysis",
            pageId: "Gen AI - Sentiment Analysis",
        },
        defaultQueryParams: {
            model: DEFAULT_LLM,
            duration: AI_TRACKER_DEFAULT_DURATION,
            topic: DEFAULT_TOPIC,
            isDurationChanged: false,
        },
        pageTitle: "llm.homepage.title",
        reloadOnSearch: false,
        isPrimarySidebarAlwaysOpen: true,
    },
};
